import { IS_ANDROID_MOBILE, IS_IOS_MOBILE, IS_WEBVIEW, IS_WEBVIEW_FROM_UA } from 'reactApp/appHelpers/configHelpers';
import { maxFileNameLength } from 'reactApp/appHelpers/featuresHelpers/features/thumbLogs';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import { isEBook } from 'reactApp/modules/file/isEBook';
import { type IPromoEpub, type IPromoSecondClick, EPromoType } from 'reactApp/modules/promo/promo.types';
import { EpubPromoType } from 'reactApp/modules/promo/sagas/epub.saga';
import type { EViewMode } from 'reactApp/modules/settings/settings.types';
import type { CloudItem } from 'reactApp/modules/storage/storage.types';
import { WorkspaceSuperAppService } from 'reactApp/services';
import type { Kind } from 'reactApp/types/Tree';
import { TipEpubContent } from 'reactApp/ui/Datalist/Item/TipEpubContent';
import { TipSecondClickContent } from 'reactApp/ui/Datalist/Item/TipSecondClickContent';
import { sendXray } from 'reactApp/utils/ga';
import { sendThumbDwh } from 'reactApp/utils/thumbDwh';

interface ITip {
    id: string;
    getTipContent: typeof TipSecondClickContent | typeof TipEpubContent;
    onHideClick: () => void;
    onShow: (id: string) => void;
}

function promoToTip(promo: IPromoSecondClick | IPromoEpub): ITip[] | null {
    return [
        {
            id: promo.promoId,
            getTipContent: promo.type === EPromoType.epubPromo ? TipEpubContent : TipSecondClickContent,
            onHideClick: () => promo.onClose(),
            onShow: (_) => {
                if (promo.type !== EPromoType.epubPromo) {
                    return;
                }
                promo.onShow();
                promo.onMarkShown(EpubPromoType.downloadList);
                sendXray(['datalist', 'ebook', 'tipdownload', 'show']);
            },
        },
    ];
}

export const getSelectedCount = (key: string, columnCount: number): number => {
    switch (key) {
        case 'ArrowDown':
            return columnCount;
        case 'ArrowUp':
            return -columnCount;
        case 'ArrowLeft':
            return -1;
        case 'ArrowRight':
            return 1;
        default:
            return 0;
    }
};

const extractThumbInfoFromSrc = (src?: string) => {
    if (!src) {
        return {};
    }

    try {
        const urlObj = new URL(src);
        const pathParts = urlObj.pathname.split('/').filter(Boolean);

        const thumbSize = pathParts[1] || '';
        const fileName = decodeURIComponent(pathParts[pathParts.length - 1] || '');
        const parentFolder = decodeURIComponent(pathParts.length > 3 ? pathParts[pathParts.length - 2] : '');
        const name = `${parentFolder}/${fileName}`;

        return {
            domain: urlObj.hostname,
            thumbSize,
            name: name.length > maxFileNameLength ? name.slice(name.length - maxFileNameLength) : name,
        };
    } catch (_) {
        return {};
    }
};

type AppEnv = 'web' | 'wv' | 'ios-wv' | 'and-wv' | 'touch' | 'and' | 'ios' | 'sapp';

const getEnv = (): AppEnv => {
    if (WorkspaceSuperAppService.isEnabled) {
        return 'sapp';
    } else if (IS_WEBVIEW || IS_WEBVIEW_FROM_UA) {
        if (IS_IOS_MOBILE) {
            return 'ios-wv';
        } else if (IS_ANDROID_MOBILE) {
            return 'and-wv';
        }
        return 'wv';
    } else if (EnvironmentSelectors.isMobile()) {
        if (IS_IOS_MOBILE) {
            return 'ios';
        } else if (IS_ANDROID_MOBILE) {
            return 'and';
        }
        return 'touch';
    }
    return 'web';
};

type DatalistThumbAction = 'load' | 'fail';

export const sendDatalistThumbDwh = (
    action: DatalistThumbAction,
    view: EViewMode,
    kind: Kind | undefined,
    loadingTime: number | undefined,
    src?: string | undefined
) => {
    const { domain, thumbSize, name } = extractThumbInfoFromSrc(src);
    const dwh = {
        kind,
        domain,
        thumbSize,
        view,
        env: getEnv(),
        time: loadingTime,
        name,
    };

    sendThumbDwh({
        action,
        dwh,
        dwh_enabled: true,
        dwh_enabled_fields: [['view', 'kind', 'env']],
    });
};

export const logHandler = ({ radars, radarName }: { radars?: string[]; radarName: string }): void => {
    if (radars) {
        for (const element of radars) {
            const r = element.split('=');

            sendXray('datalist', {
                [`${radarName}_${r[0]}`]: Number(r[1]),
            });
        }
    } else {
        sendXray(['datalist', radarName]);
    }
};

interface IProps {
    selectedCount: number;
    isDragging: boolean;
    disableTips: boolean;
    promo?: IPromoSecondClick | IPromoEpub;
    selectedItems: (CloudItem | undefined)[];
}

export const getShowPromoTip = ({ selectedCount, isDragging, disableTips, promo, selectedItems }: IProps) => {
    if (!promo || isDragging || disableTips) {
        return null;
    }

    if (promo.type === EPromoType.secondClick && !selectedCount) {
        return null;
    }

    if (promo.type === EPromoType.epubPromo && (selectedItems?.length !== 1 || !selectedItems?.[0] || !isEBook(selectedItems?.[0]))) {
        return null;
    }

    return promoToTip(promo) || null;
};
